import { Sunrise } from "lucide-react";
import { type Identifier, useGetMany, useTranslate } from "ra-core";
import { useMemo } from "react";
import { Link } from "react-router";
import { Card } from "@/components/ui/card";

import { TaskDueCell } from "../tasks/TaskRowFields";
import { isOverdue } from "../tasks/tasksPredicate";
import { useTaskDueBucket } from "../tasks/useTaskDueBucket";
import type { Company, Contact, Task } from "../types";
import { useDashboardScope } from "./useDashboardScope";

/**
 * Rows rendered before the widget defers to the full task list. The column is
 * a narrow one: past this, scanning is worse than clicking through.
 */
const MAX_ROWS = 8;

const DUE_DATE_ASC = { field: "due_date", order: "ASC" } as const;

/**
 * Overdue and due-today tasks for the scope currently selected on the
 * dashboard. Deliberately nothing later: "this week" and "later" belong to the
 * /tasks page, and are what make a task widget in a side column unreadable.
 */
export const MyDay = () => {
  const translate = useTranslate();
  const { salesId, isTeam, isPending: isScopePending } = useDashboardScope();

  const scopeFilter = useMemo(
    () => (isTeam ? {} : { sales_id: salesId }),
    [isTeam, salesId],
  );

  /*
    Two narrow, indexed range queries rather than one wide window. Fetching by
    "due_date <= end of today" and filtering in the browser looks equivalent but
    is not: sorted ascending, that window fills with the OLDEST rows in the
    table -- overwhelmingly long-completed ones -- so on a mature dataset the
    widget renders empty while genuinely overdue work exists. Each bucket query
    already excludes done tasks server-side and reports a real total.
  */
  const overdue = useTaskDueBucket(
    "overdue",
    scopeFilter,
    DUE_DATE_ASC,
    !isScopePending,
  );
  const today = useTaskDueBucket(
    "today",
    scopeFilter,
    DUE_DATE_ASC,
    !isScopePending,
  );

  const isPending = overdue.isPending || today.isPending;

  // Overdue leads: it is the half that needs a decision today.
  const dueNowTasks = useMemo(
    () => [...overdue.tasks, ...today.tasks],
    [overdue.tasks, today.tasks],
  );

  const visibleTasks = dueNowTasks.slice(0, MAX_ROWS);
  // Counted off the server-side totals, so the footer is right even though the
  // fetched window is capped well below the backlog.
  const remainingCount = Math.max(
    0,
    overdue.total + today.total - visibleTasks.length,
  );

  // A task no longer always has a contact: it can be aimed at the account or at
  // a deal instead. Only the ones that do get looked up.
  const contactIds = useMemo(
    () => [
      ...new Set(
        visibleTasks
          .map((task) => task.contact_id)
          .filter((id): id is Identifier => id != null),
      ),
    ],
    [visibleTasks],
  );

  const { data: contacts } = useGetMany<Contact>(
    "contacts",
    { ids: contactIds },
    { enabled: contactIds.length > 0 },
  );

  const contactsById = useMemo(
    () =>
      new Map((contacts ?? []).map((contact) => [String(contact.id), contact])),
    [contacts],
  );

  /*
    The account is read off the task itself rather than through its contact: a
    task can now be aimed at the account or at a deal, and those have no contact
    to borrow a company name from.
  */
  const companyIds = useMemo(
    () => [
      ...new Set(
        visibleTasks
          .map((task) => task.company_id)
          .filter((id): id is Identifier => id != null),
      ),
    ],
    [visibleTasks],
  );

  const { data: companies } = useGetMany<Company>(
    "companies",
    { ids: companyIds },
    { enabled: companyIds.length > 0 },
  );

  const companiesById = useMemo(
    () =>
      new Map(
        (companies ?? []).map((company) => [String(company.id), company]),
      ),
    [companies],
  );

  /*
    One row, three possible targets. The most specific one wins, so a task about
    a deal opens that deal rather than the account it happens to belong to.
    Without this an account-targeted task linked to /contacts/undefined/show.
  */
  const taskLink = (task: Task) => {
    if (task.deal_id != null) return `/deals/${task.deal_id}/show`;
    if (task.contact_id != null) return `/contacts/${task.contact_id}/show`;
    if (task.company_id != null) return `/companies/${task.company_id}/show`;
    return "/tasks";
  };

  if (isPending) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <div className="mr-3 flex">
          <Sunrise className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground flex-1">
          {translate("crm.dashboard.my_day.title")}
        </h2>
      </div>
      <Card className="p-4 gap-2">
        {visibleTasks.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.dashboard.my_day.empty")}
          </p>
        ) : (
          <ul className="flex flex-col divide-y">
            {visibleTasks.map((task) => {
              const contact = contactsById.get(String(task.contact_id));
              const contactName = contact
                ? `${contact.first_name} ${contact.last_name}`
                : undefined;
              // The account is what the row is scanned by, so it leads. The
              // contact is one level of detail down and rides in the tooltip.
              const accountLabel =
                companiesById.get(String(task.company_id))?.name ??
                contact?.company_name ??
                contactName;
              const taskIsOverdue = isOverdue(task.due_date);

              return (
                <li key={task.id} className="py-2 first:pt-0 last:pb-0">
                  <Link
                    to={taskLink(task)}
                    title={contactName}
                    className="flex items-baseline gap-2 text-sm hover:underline"
                  >
                    {accountLabel && (
                      <span className="font-medium shrink-0 max-w-[45%] truncate">
                        {accountLabel}
                      </span>
                    )}
                    <span className="flex-1 truncate text-muted-foreground">
                      {task.text}
                    </span>
                    {/*
                      The same cell the task list uses, so a task cannot read
                      one way here and another way there. A task due today is
                      placed by its time alone; an overdue one spans days, so
                      the cell states its age instead — the time on its own
                      would read as today.
                    */}
                    <TaskDueCell
                      task={task}
                      timeOnly={!taskIsOverdue}
                      className="text-xs"
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
        {remainingCount > 0 && (
          <Link
            to="/tasks"
            className="text-sm underline hover:no-underline self-start"
          >
            {translate("crm.dashboard.my_day.more", {
              smart_count: remainingCount,
            })}
          </Link>
        )}
      </Card>
    </div>
  );
};
